import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../../context/CartContext';
import { products } from '../../../mocks/products';

const bpProducts = products.filter(p => p.category === 'blue-protect');

const ProductDetail = ({ product }: { product: typeof bpProducts[0] }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const { addToCart } = useCart();

  const tabLabels = [
    t('bp_prod_tab_desc'),
    t('bp_prod_tab_ing'),
    t('bp_prod_tab_use'),
    t('bp_prod_tab_reviews'),
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
      {/* Image */}
      <div className="relative rounded-2xl overflow-hidden aspect-square">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050010]/60 via-transparent to-transparent" />
        {product.badge && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-violet-700/80 border border-violet-500/40 rounded-full">
            <span className="text-white text-xs font-bold">{product.badge}</span>
          </div>
        )}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-star-fill text-violet-400 text-xs" />
          </div>
          <span className="text-white text-xs font-semibold">{product.rating}</span>
          <span className="text-white/40 text-xs">({product.reviews} {t('common_reviews')})</span>
        </div>
      </div>

      {/* Info */}
      <div>
        <p className="text-violet-400/70 text-xs font-semibold uppercase tracking-widest mb-2">{t('bp_prod_series_label')}</p>
        <h3 className="text-white font-black text-2xl lg:text-3xl mb-3">{product.name}</h3>
        <div className="flex items-center gap-4 mb-6">
          <span className="text-violet-400 font-black text-3xl">€{product.price.toFixed(2)}</span>
          <span className="text-green-400/80 text-xs font-semibold px-2 py-1 bg-green-500/10 border border-green-500/20 rounded-full">{t('bp_prod_in_stock_label')}</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-[#0a0820]/60 p-1 rounded-xl border border-violet-500/10">
          {tabLabels.map((label, i) => (
            <button
              key={label}
              onClick={() => setTab(i)}
              className={`flex-1 px-2 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                tab === i
                  ? 'bg-violet-700/50 text-violet-300 border border-violet-500/30'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[160px]">
          {tab === 0 && (
            <div>
              <p className="text-white/60 text-sm leading-relaxed mb-5">{product.fullDescription}</p>
              {product.benefits && (
                <ul className="space-y-2">
                  {product.benefits.map(b => (
                    <li key={b} className="flex items-start gap-2 text-white/55 text-xs">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-violet-400 text-sm" />
                      </div>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {tab === 1 && (
            <div>
              <div className="p-5 bg-[#0a0820]/60 border border-violet-500/15 rounded-xl mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-flask-line text-violet-400 text-sm" />
                  </div>
                  <h4 className="text-violet-300 font-semibold text-sm">{t('bp_prod_ing_label')}</h4>
                </div>
                <p className="text-white/60 text-xs leading-relaxed">{product.ingredients}</p>
              </div>
              <div className="flex items-start gap-2 p-4 bg-violet-900/15 border border-violet-500/15 rounded-xl">
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-leaf-line text-violet-400 text-sm" />
                </div>
                <p className="text-violet-300/70 text-xs leading-relaxed">
                  {t('bp_prod_ing_pure_note')}
                </p>
              </div>
            </div>
          )}
          {tab === 2 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-book-open-line text-violet-400 text-sm" />
                </div>
                <h4 className="text-violet-300 font-semibold text-sm">{t('bp_prod_use_label')}</h4>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{product.howToUse}</p>
            </div>
          )}
          {tab === 3 && (
            <div className="space-y-3">
              {product.reviewList?.map(r => (
                <div key={r.name} className="p-4 bg-[#0a0820]/50 border border-violet-500/10 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-violet-700/30 border border-violet-500/20">
                        <i className="ri-user-line text-violet-400 text-xs" />
                      </div>
                      <span className="text-white font-semibold text-xs">{r.name}</span>
                    </div>
                    <span className="text-white/30 text-xs">{r.date}</span>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(r.rating)].map((_, i) => (
                      <div key={i} className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-violet-400 text-xs" />
                      </div>
                    ))}
                  </div>
                  <p className="text-white/55 text-xs leading-relaxed">{r.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => addToCart({
              id: product.id,
              name: product.name,
              price: product.price,
              type: 'product',
              image: product.image,
            })}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-cart-line text-sm" />
            </div>
            {t('bp_prod_add_cart_btn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const BPProducts = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);

  return (
    <section id="producten" className="w-full py-20 bg-gradient-to-b from-[#050010] to-[#080018]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('bp_prod_badge')}</p>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            {t('bp_prod_title').split(',')[0]},{' '}
            <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
              {t('bp_prod_title').split(',')[1]?.trim()}
            </span>
          </h2>
          <p className="text-white/45 text-sm max-w-xl mx-auto leading-relaxed">
            {t('bp_prod_sub')}
          </p>
        </div>

        {/* Product Switcher */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {bpProducts.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActiveProduct(i)}
              className={`flex items-center gap-3 px-5 py-3 rounded-2xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeProduct === i
                  ? 'bg-violet-700/30 border-violet-500/50 text-violet-300'
                  : 'bg-[#0a0820]/40 border-violet-500/15 text-white/45 hover:text-white/70 hover:border-violet-500/30'
              }`}
            >
              <img
                src={p.image}
                alt={p.name}
                className="w-8 h-8 rounded-lg object-cover object-top"
              />
              <span>{p.name.replace('Blue Protect ', '')}</span>
              {p.badge && (
                <span className="px-1.5 py-0.5 bg-violet-700/50 text-violet-300 text-xs rounded-full">{p.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Active Product Detail */}
        <div className="bg-[#080018]/60 border border-violet-500/15 rounded-3xl p-6 lg:p-10">
          <ProductDetail product={bpProducts[activeProduct]} />
        </div>

        {/* Grid Preview */}
        <div className="mt-12">
          <p className="text-white/30 text-xs text-center mb-6 uppercase tracking-widest">{t('bp_prod_overview_label')}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {bpProducts.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActiveProduct(i)}
                className={`relative rounded-2xl overflow-hidden aspect-square cursor-pointer transition-all ${
                  activeProduct === i
                    ? 'ring-2 ring-violet-500/60 ring-offset-2 ring-offset-[#050010]'
                    : 'opacity-60 hover:opacity-90'
                }`}
              >
                <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-semibold text-xs leading-tight">{p.name.replace('Blue Protect ', '')}</p>
                  <p className="text-violet-400 font-bold text-sm mt-0.5">€{p.price.toFixed(2)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
